import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import ContainerPage from "../../components/Containers/ContainerPage";
import ContentApprovalPreview from "../../components/ContentApproval/ContentApprovalPreview";
import ContentApprovalLoader from "../../components/ContentApproval/ContentApprovalLoader";
import {
  contentApprovalLayoutRootSx,
  contentApprovalMainSx,
  CONTENT_APPROVAL_WORKSPACE_BG,
} from "../../components/ContentApproval/contentApprovalLayout";
import {
  startDecideContentApproval,
  startGetContentApprovalPayload,
  startSubmitContentApprovalComment,
} from "../../actions/contentApproval";
import ContentApprovalSidebar from "./components/ContentApprovalSidebar";
import ContentApprovalCreativeCards from "./components/ContentApprovalCreativeCards";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import {
  CONTENT_APPROVAL_CONFIRM_APPROVE_DESCRIPTION,
  CONTENT_APPROVAL_CONFIRM_APPROVE_TITLE,
  CONTENT_APPROVAL_APPROVE_BUTTON,
  CONTENT_APPROVAL_CONTENT_APPROVED_TITLE,
  CONTENT_APPROVAL_COMMENT_SENT_SUCCESS,
  CONTENT_APPROVAL_REVIEW_NEXT_CONTENT_BUTTON,
  CONTENT_APPROVAL_ANOTHER_COMMENT_BUTTON,
  CONTENT_APPROVAL_ALL_CONTENTS_REVIEWED,
  CONTENT_APPROVAL_REVIEW_NEXT_DESCRIPTION,
  CONTENT_APPROVAL_REVIEW_NEXT_DESCRIPTION_SINGLE,
  CONTENT_APPROVAL_EXPIRED_DESCRIPTION,
  CONTENT_APPROVAL_EXPIRED_TITLE,
  CONTENT_APPROVAL_INVALID_DESCRIPTION,
  CONTENT_APPROVAL_INVALID_TITLE,
  CONTENT_APPROVAL_LOCKED_STATUS_APPROVED,
  CONTENT_APPROVAL_PAGE_TITLE,
  BUTTON_GO_BACK,
  OK,
  TOKEN_EXPIRED,
  TOKEN_INVALID,
} from "../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import {
  countPendingContentApprovals,
  filterVisibleContentApprovalPayloads,
  findContentApprovalPayloadIndex,
  findNextPendingContentApprovalIndex,
  getContentApprovalCurrentDraftIDsByContentID,
  getContentApprovalStateForContent,
  getInitialVisibleContentApprovalContentIDs,
  getReviewerOwnStatusForContent,
} from "../../components/ContentApproval/contentApprovalUtils";
import { mergeReviewerApprovalsIntoContentMap } from "../../components/ContentApproval/approvalReviewersUtils";

const ContentApproval = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const appTheme = useSelectorApp((state) => state.ui.theme);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
  const [payloads, setPayloads] = useState([]);
  const [approval, setApproval] = useState(null);
  const [comments, setComments] = useState([]);
  const [visibleContentIDs, setVisibleContentIDs] = useState([]);
  const [accessErrorCode, setAccessErrorCode] = useState("");
  const [selectedPayloadIndex, setSelectedPayloadIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [currentReviewer, setCurrentReviewer] = useState(null);

  const token = searchParams.get("token") || "";

  const visiblePayloads = useMemo(
    () => filterVisibleContentApprovalPayloads(payloads, visibleContentIDs),
    [payloads, visibleContentIDs]
  );

  const currentDraftIDsByContentID = useMemo(
    () =>
      getContentApprovalCurrentDraftIDsByContentID(visiblePayloads, approval),
    [approval, visiblePayloads]
  );

  const loadPayload = useCallback(async () => {
    if (!token) {
      setAccessErrorCode(TOKEN_INVALID);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const response = await dispatch(startGetContentApprovalPayload(token));

    if (!response?.ok) {
      setAccessErrorCode(response?.code || TOKEN_INVALID);
      setPayloads([]);
      setApproval(null);
      setComments([]);
      setVisibleContentIDs([]);
      setCurrentReviewer(null);
      setIsLoading(false);
      return;
    }

    const nextPayloads = Array.isArray(response.payloads)
      ? response.payloads
      : [];
    const initialVisibleContentIDs =
      getInitialVisibleContentApprovalContentIDs(nextPayloads);
    const initialVisiblePayloads = filterVisibleContentApprovalPayloads(
      nextPayloads,
      initialVisibleContentIDs
    );
    const contentIDParam = searchParams.get("contentID") || "";
    const preselectedIndex = contentIDParam
      ? findContentApprovalPayloadIndex(initialVisiblePayloads, contentIDParam)
      : -1;

    setPayloads(nextPayloads);
    setApproval(response.approval || null);
    setComments(response.comments || []);
    setCurrentReviewer(response.currentReviewer || null);
    setVisibleContentIDs(initialVisibleContentIDs);
    setAccessErrorCode("");
    setSelectedPayloadIndex(preselectedIndex >= 0 ? preselectedIndex : 0);
    setCarouselIndex(0);
    setIsLoading(false);
  }, [dispatch, searchParams, token]);

  useEffect(() => {
    loadPayload();
  }, [loadPayload]);

  useEffect(() => {
    if (
      visiblePayloads.length === 0 ||
      selectedPayloadIndex < visiblePayloads.length
    ) {
      return;
    }

    setSelectedPayloadIndex(0);
  }, [selectedPayloadIndex, visiblePayloads.length]);

  const payload =
    visiblePayloads[selectedPayloadIndex] || visiblePayloads[0] || null;

  const currentReviewerID = currentReviewer?.reviewerID || "";

  const selectedApprovalState = useMemo(() => {
    return getContentApprovalStateForContent({
      approvalData: approval,
      contentID: payload?.contentID,
      fallbackStatus: payload?.status || null,
      fallbackCurrentDraftID: payload?.currentDraftID || null,
      fallbackDraftRevision: payload?.draftRevision || 0,
    });
  }, [approval, payload]);

  const visibleApprovalStatus = useMemo(() => {
    if (!payload?.contentID) {
      return null;
    }

    return getReviewerOwnStatusForContent({
      approvalData: approval,
      contentID: payload.contentID,
      reviewerID: currentReviewerID,
      fallbackStatus: payload?.status || null,
      fallbackCurrentDraftID: payload?.currentDraftID || null,
      fallbackDraftRevision: payload?.draftRevision || 0,
    });
  }, [approval, currentReviewerID, payload]);

  const getStatusForPayload = useCallback(
    ({ payload: cardPayload }) =>
      getReviewerOwnStatusForContent({
        approvalData: approval,
        contentID: cardPayload?.contentID,
        reviewerID: currentReviewerID,
        fallbackStatus: cardPayload?.status || null,
        fallbackCurrentDraftID: cardPayload?.currentDraftID || null,
        fallbackDraftRevision: cardPayload?.draftRevision || 0,
      }),
    [approval, currentReviewerID]
  );

  const commentsLocked = useMemo(() => {
    if (!payload?.contentID) {
      return true;
    }
    return visibleApprovalStatus === "approved";
  }, [payload?.contentID, visibleApprovalStatus]);

  const isCarousel =
    payload?.type === "carousel" && Array.isArray(payload?.urls);
  const carouselUrls = isCarousel ? payload.urls : [];
  const currentUrl = isCarousel
    ? carouselUrls[carouselIndex] || payload?.url
    : payload?.url;

  const onSelectCreative = useCallback((index) => {
    setSelectedPayloadIndex(index);
    setCarouselIndex(0);
  }, []);

  const syncPayloadApprovalState = useCallback((contentID, nextState = {}) => {
    if (!contentID) {
      return;
    }

    const normalizedContentID = String(contentID);

    setPayloads((current) =>
      current.map((item) =>
        item?.contentID === contentID
          ? {
              ...item,
              ...(nextState.status !== undefined
                ? { status: nextState.status }
                : {}),
              ...(nextState.currentDraftID !== undefined
                ? { currentDraftID: nextState.currentDraftID }
                : {}),
              ...(nextState.draftRevision !== undefined
                ? { draftRevision: nextState.draftRevision }
                : {}),
            }
          : item
      )
    );

    setApproval((current) => {
      if (!current) {
        return current;
      }

      const currentContentState =
        current?.contentStates?.[normalizedContentID] || {};

      return {
        ...current,
        contentStates: {
          ...(current.contentStates || {}),
          [normalizedContentID]: {
            ...currentContentState,
            ...(nextState.status !== undefined
              ? { status: nextState.status }
              : {}),
            ...(nextState.currentDraftID !== undefined
              ? { currentDraftID: nextState.currentDraftID }
              : {}),
            ...(nextState.draftRevision !== undefined
              ? { draftRevision: nextState.draftRevision }
              : {}),
          },
        },
      };
    });
  }, []);

  const syncReviewerApprovalsForContent = useCallback(
    (contentID, reviewerApprovals = {}) => {
      if (!contentID || !reviewerApprovals) {
        return;
      }

      const normalizedContentID = String(contentID);
      const normalizedReviewerApprovals = mergeReviewerApprovalsIntoContentMap({
        currentApprovals: {},
        reviewerApprovals,
      });

      if (Object.keys(normalizedReviewerApprovals).length === 0) {
        return;
      }

      setApproval((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          reviewerApprovalsByContent: {
            ...(current.reviewerApprovalsByContent || {}),
            [normalizedContentID]: mergeReviewerApprovalsIntoContentMap({
              currentApprovals:
                current.reviewerApprovalsByContent?.[normalizedContentID] || {},
              reviewerApprovals,
            }),
          },
        };
      });
    },
    []
  );

  const handlePostReviewAction = useCallback(
    async ({ contentID, nextStatus, reviewerOwnStatus, actionType }) => {
      syncPayloadApprovalState(contentID, { status: nextStatus });

      const individualStatus =
        reviewerOwnStatus ??
        (actionType === "approve" ? "approved" : nextStatus);

      const statusOverrides = {
        [String(contentID)]: { status: individualStatus },
      };
      const pendingCount = countPendingContentApprovals({
        payloads: visiblePayloads,
        approvalData: approval,
        statusOverrides,
        reviewerID: currentReviewerID,
      });
      const nextIndex = findNextPendingContentApprovalIndex({
        payloads: visiblePayloads,
        approvalData: approval,
        currentContentID: contentID,
        statusOverrides,
        reviewerID: currentReviewerID,
      });
      const showNextOption =
        visiblePayloads.length > 1 && nextIndex !== null && pendingCount > 0;

      const successTitle =
        actionType === "approve"
          ? t(CONTENT_APPROVAL_CONTENT_APPROVED_TITLE)
          : t(CONTENT_APPROVAL_COMMENT_SENT_SUCCESS);

      if (showNextOption) {
        const isCommentAction = actionType === "comment";
        const confirmResult = await ConfirmAlert({
          title: successTitle,
          text: t(
            pendingCount === 1
              ? CONTENT_APPROVAL_REVIEW_NEXT_DESCRIPTION_SINGLE
              : CONTENT_APPROVAL_REVIEW_NEXT_DESCRIPTION,
            { count: pendingCount }
          ),
          icon: ALERT_ICON_TYPE_SUCCESS,
          confirmButtonText: t(CONTENT_APPROVAL_REVIEW_NEXT_CONTENT_BUTTON),
          showCancelButton: isCommentAction,
          cancelButtonText: t(CONTENT_APPROVAL_ANOTHER_COMMENT_BUTTON),
        });

        if (confirmResult.isConfirmed) {
          onSelectCreative(nextIndex);
        }
        return;
      }

      await SimpleAlert({
        title: t(CONTENT_APPROVAL_ALL_CONTENTS_REVIEWED),
        icon: ALERT_ICON_TYPE_SUCCESS,
        confirmButtonText: t(OK),
      });
    },
    [
      approval,
      currentReviewerID,
      onSelectCreative,
      syncPayloadApprovalState,
      t,
      visiblePayloads,
    ]
  );

  const onHandleComment = async ({ body }) => {
    if (!payload?.contentID) {
      return { ok: false };
    }

    const contentID = payload.contentID;

    setIsSubmittingComment(true);
    const response = await dispatch(
      startSubmitContentApprovalComment({
        token,
        body,
        contentID,
      })
    );
    setIsSubmittingComment(false);

    if (response?.ok && response?.comment) {
      setComments((current) => [...current, response.comment]);
      if (response?.reviewerApprovals) {
        syncReviewerApprovalsForContent(contentID, response.reviewerApprovals);
      }
      await handlePostReviewAction({
        contentID,
        nextStatus: response.status || "rejected",
        reviewerOwnStatus: "rejected",
        actionType: "comment",
      });
    }

    return response;
  };

  const onHandleApprove = async () => {
    if (!payload?.contentID) {
      return;
    }

    const contentID = payload.contentID;

    const confirmResult = await ConfirmAlert({
      title: t(CONTENT_APPROVAL_CONFIRM_APPROVE_TITLE),
      text: t(CONTENT_APPROVAL_CONFIRM_APPROVE_DESCRIPTION),
      confirmButtonText: t(CONTENT_APPROVAL_APPROVE_BUTTON),
      cancelButtonText: t(BUTTON_GO_BACK),
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setIsSubmittingDecision(true);
    const response = await dispatch(
      startDecideContentApproval({
        token,
        contentID,
        decision: "approved",
      })
    );
    setIsSubmittingDecision(false);

    if (response?.ok) {
      if (response?.comment) {
        setComments((current) => [...current, response.comment]);
      }
      if (response?.reviewerApprovals) {
        syncReviewerApprovalsForContent(contentID, response.reviewerApprovals);
      }
      const reviewerOwnStatus =
        response?.reviewerApprovals?.[currentReviewerID]?.status || "approved";

      await handlePostReviewAction({
        contentID,
        nextStatus: response.status || "approved",
        reviewerOwnStatus,
        actionType: "approve",
      });
    }
  };

  const renderErrorState = () => {
    const isExpired = accessErrorCode === TOKEN_EXPIRED;
    return (
      <Box sx={{ maxWidth: 760, m: "auto", p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t(
            isExpired
              ? CONTENT_APPROVAL_EXPIRED_TITLE
              : CONTENT_APPROVAL_INVALID_TITLE
          )}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t(
            isExpired
              ? CONTENT_APPROVAL_EXPIRED_DESCRIPTION
              : CONTENT_APPROVAL_INVALID_DESCRIPTION
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <ContainerPage
      hasMarginBottom={false}
      sx={{
        width: "100%",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        p: 0,
        px: 0,
        pb: 0,
        bgcolor: CONTENT_APPROVAL_WORKSPACE_BG,
      }}
    >
      <Helmet>
        <title>{`${appTheme?.nameBusiness} - ${t(CONTENT_APPROVAL_PAGE_TITLE)}`}</title>
      </Helmet>

      {isLoading ? (
        <ContentApprovalLoader open />
      ) : accessErrorCode ? (
        renderErrorState()
      ) : (
        <Box sx={contentApprovalLayoutRootSx}>
          <Box
            sx={{
              ...contentApprovalMainSx,
              overflowY: "auto",
              p: { xs: 2, md: 3 },
            }}
          >
            <ContentApprovalCreativeCards
              payloads={visiblePayloads}
              selectedIndex={selectedPayloadIndex}
              onSelect={onSelectCreative}
              getStatusForPayload={getStatusForPayload}
            />
            <ContentApprovalPreview
              currentUrl={currentUrl}
              isCarousel={isCarousel && carouselUrls.length > 1}
              carouselIndex={carouselIndex}
              carouselCount={carouselUrls.length}
              onPrevCarousel={() =>
                setCarouselIndex((prev) => Math.max(0, prev - 1))
              }
              onNextCarousel={() =>
                setCarouselIndex((prev) =>
                  Math.min(carouselUrls.length - 1, prev + 1)
                )
              }
              showVersionBadge={false}
              showCardFrame={false}
              compact={visiblePayloads.length > 1}
              mediaWidth={payload?.width}
              mediaHeight={payload?.height}
              lockedMessage={
                commentsLocked ? t(CONTENT_APPROVAL_LOCKED_STATUS_APPROVED) : ""
              }
            />
          </Box>

          <ContentApprovalSidebar
            comments={comments}
            selectedContentID={payload?.contentID || ""}
            currentDraftID={selectedApprovalState.currentDraftID || ""}
            currentDraftIDsByContentID={currentDraftIDsByContentID}
            commentsLocked={commentsLocked}
            isSubmittingComment={isSubmittingComment}
            isSubmittingDecision={isSubmittingDecision}
            sentAt={approval?.createdAt || null}
            approvalStatus={visibleApprovalStatus}
            reviewers={approval?.reviewers || []}
            currentReviewer={currentReviewer}
            onSubmitComment={onHandleComment}
            onSubmitApprove={onHandleApprove}
          />
        </Box>
      )}
    </ContainerPage>
  );
};

export default ContentApproval;
